// Member Detail (Profile View) Page for Iron Forums

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { profilesAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { publicRoleBadgeClass, publicRoleLabel } from '../utils/roles';
import RequestMeetingModal from '../components/RequestMeetingModal';
import { 
  ArrowLeft,
  MapPin, 
  Briefcase, 
  Calendar,
  Heart,
  Users,
  Target,
  MessageSquare,
  Bot,
  User as UserIcon,
  Mail,
  BookOpen,
  Lock,
  Building2,
  CalendarPlus,
  Globe,
} from 'lucide-react';
import type { UserProfile } from '../types';

export default function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [member, setMember] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchMember(parseInt(id));
    }
  }, [id]);

  const fetchMember = async (memberId: number) => {
    try {
      const data = await profilesAPI.getById(memberId);
      setMember(data);
    } catch (err) {
      console.error('Error fetching member:', err);
      setError('Member not found');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAskJarvis = () => {
    navigate('/jarvis', { 
      state: { 
        message: `Tell me more about ${member?.full_name}. What can they help with?` 
      } 
    });
  };

  // Go back to wherever the user actually came from (admin members, roster,
  // directory, a Jarvis link, etc.) instead of always landing on the
  // directory. `history.state.idx` is set by react-router's browser history
  // for every entry, including the very first one in the session (idx 0) —
  // if we're there, there's no in-app back target, so fall back.
  const handleBack = () => {
    const historyIndex = (window.history.state as { idx?: number } | null)?.idx;
    if (historyIndex) {
      navigate(-1);
    } else {
      navigate('/members');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
        <UserIcon className="mx-auto text-gray-300" size={48} />
        <h3 className="mt-4 text-lg font-medium text-gray-700">
          {error || 'Member not found'}
        </h3>
        <button
          onClick={handleBack}
          className="mt-4 inline-flex items-center gap-2 text-navy hover:underline"
        >
          <ArrowLeft size={18} />
          Back
        </button>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === member.id;
  const redacted = member.visibility_redacted || [];
  const isRedacted = (section: string) => redacted.includes(section);

  // Small placeholder card so a section the member chose to hide reads as
  // "private" rather than simply missing (sparse profiles otherwise look
  // broken or empty).
  const privateSection = (title: string) => (
    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
      <h2 className="text-lg font-semibold text-gray-400 flex items-center gap-2">
        <Lock size={18} />
        {title}
      </h2>
      <p className="text-sm text-gray-400 mt-1">
        {member.first_name} has chosen to keep this section private.
      </p>
    </div>
  );

  // Primary site plus any extra businesses' sites, in one flat list.
  const memberWebsites = [member.website, ...(member.additional_websites || [])]
    .filter((site): site is string => Boolean(site));

  // True when the main column would render nothing at all — no visible
  // content and nothing redacted — so we can show a friendly empty state.
  const mainColumnEmpty =
    !isRedacted('bio') && !member.bio &&
    !isRedacted('business') && !(member.industry || member.business_type || member.years_in_business || memberWebsites.length) &&
    !isRedacted('expertise') && !(member.areas_of_expertise?.length) &&
    !isRedacted('growth') && !(member.growth_areas?.length) &&
    !isRedacted('faith') && !member.faith_journey && !member.why_iron_forums;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-gray-600 hover:text-navy transition pointer-coarse:py-2.5 pointer-coarse:-my-2.5"
      >
        <ArrowLeft size={18} />
        <span>Back</span>
      </button>

      {/* Profile Header */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Banner */}
        <div className="h-24 bg-gradient-to-r from-navy to-navy-light"></div>

        {/* Profile Info */}
        <div className="px-6 pb-6">
          {/* Avatar - overlaps banner */}
          <div className="-mt-12 mb-4">
            <div className="w-24 h-24 bg-off-white rounded-full border-4 border-white flex items-center justify-center flex-shrink-0 shadow-md">
              {member.avatar_url ? (
                <img 
                  src={member.avatar_url} 
                  alt={member.full_name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <UserIcon className="text-navy" size={40} />
              )}
            </div>
          </div>

          {/* Name, Role & Actions */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            {/* Name & Role */}
            <div>
              <h1 className="text-2xl font-bold text-navy">
                {member.full_name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span className={`text-sm font-medium px-3 py-1 rounded-full ${publicRoleBadgeClass(member.role, member.snapshot_type)}`}>
                  {publicRoleLabel(member.role, member.snapshot_type)}
                </span>
                {member.chapter && (
                  <span className="text-gray-500 text-sm">{member.chapter}</span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full md:w-auto">
              {isOwnProfile ? (
                <Link
                  to="/settings"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg hover:bg-navy-dark transition whitespace-nowrap"
                >
                  Edit Profile
                </Link>
              ) : (
                <>
                  <Link
                    to={`/messages?to=${member.id}`}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg hover:bg-navy-dark transition whitespace-nowrap"
                  >
                    <MessageSquare size={18} className="shrink-0" />
                    Message
                  </Link>
                  <button
                    type="button"
                    onClick={() => setShowMeetingModal(true)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-navy text-navy rounded-lg hover:bg-off-white transition whitespace-nowrap"
                  >
                    <CalendarPlus size={18} className="shrink-0" />
                    Request a meeting
                  </button>
                  <button
                    type="button"
                    onClick={handleAskJarvis}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-navy text-navy rounded-lg hover:bg-off-white transition whitespace-nowrap"
                  >
                    <Bot size={18} className="shrink-0" />
                    Ask IF
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Empty state: nothing filled in and nothing private */}
          {mainColumnEmpty && (
            <div className="bg-white rounded-2xl p-10 shadow-sm text-center">
              <UserIcon className="mx-auto text-gray-300" size={40} />
              <p className="mt-3 text-gray-500">
                {member.first_name} hasn't filled out their profile yet.
              </p>
              {!isOwnProfile && (
                <p className="text-sm text-gray-400 mt-1">
                  You can still send them a message or request a meeting.
                </p>
              )}
            </div>
          )}

          {/* Bio */}
          {isRedacted('bio') && privateSection('About')}
          {!isRedacted('bio') && member.bio && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-3">About</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{member.bio}</p>
            </div>
          )}

          {/* Business Info */}
          {isRedacted('business') && privateSection('Business & Career')}
          {!isRedacted('business') && (member.industry || member.business_type || member.years_in_business || memberWebsites.length > 0) && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                <Briefcase size={20} />
                Business & Career
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {member.industry && (
                  <div>
                    <p className="text-sm text-gray-500">Industry</p>
                    <p className="font-medium text-gray-800">{member.industry}</p>
                  </div>
                )}
                {member.business_type && (
                  <div>
                    <p className="text-sm text-gray-500">Business Type</p>
                    <p className="font-medium text-gray-800">{member.business_type}</p>
                  </div>
                )}
                {member.years_in_business && (
                  <div>
                    <p className="text-sm text-gray-500">Experience</p>
                    <p className="font-medium text-gray-800">{member.years_in_business} years</p>
                  </div>
                )}
              </div>
              {memberWebsites.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500 mb-1">
                    {memberWebsites.length === 1 ? 'Website' : 'Websites'}
                  </p>
                  <div className="flex flex-col gap-1">
                    {memberWebsites.map((site) => (
                      <a
                        key={site}
                        href={site}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-navy hover:text-gold transition"
                      >
                        <Globe size={14} />
                        {site.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Areas of Expertise */}
          {isRedacted('expertise') && privateSection('Areas of Expertise')}
          {!isRedacted('expertise') && member.areas_of_expertise && member.areas_of_expertise.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                <Target size={20} />
                Areas of Expertise
              </h2>
              <p className="text-sm text-gray-500 mb-3">
                {member.first_name} can help others with:
              </p>
              <div className="flex flex-wrap gap-2">
                {member.areas_of_expertise.map((exp, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1.5 bg-off-white text-navy text-sm rounded-full font-medium"
                  >
                    {exp}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Growth Areas — hidden entirely when redacted (no placeholder card:
              this section is private-by-default, so a "hidden" card for every
              member would just be noise). */}
          {!isRedacted('growth') && member.growth_areas && member.growth_areas.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                <Target size={20} className="text-gold" />
                Growth Areas
              </h2>
              <p className="text-sm text-gray-500 mb-3">
                {member.first_name} is working to grow in:
              </p>
              <div className="flex flex-wrap gap-2">
                {member.growth_areas.map((area, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1.5 bg-gold/10 text-gold text-sm rounded-full font-medium"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Faith Journey */}
          {isRedacted('faith') && privateSection('Faith Journey')}
          {!isRedacted('faith') && member.faith_journey && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                <BookOpen size={20} />
                Faith Journey
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">{member.faith_journey}</p>
            </div>
          )}

          {/* Why they're seeking Christian business community */}
          {!isRedacted('faith') && member.why_iron_forums && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                <Users size={20} />
                Seeking Christian business community
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">{member.why_iron_forums}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact & Location */}
          {isRedacted('contact') && privateSection('Details')}
          {!isRedacted('contact') && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-4">Details</h2>
              <div className="space-y-4">
                {(member.location_city || member.location_state || member.location_country) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="text-gray-400 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-medium text-gray-800">
                        {[member.location_city, member.location_state, member.location_country].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                )}

                {member.forum_location && (
                  <div className="flex items-start gap-3">
                    <Building2 className="text-gray-400 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm text-gray-500">Forum</p>
                      <p className="font-medium text-gray-800">
                        {member.forum_location.name}
                      </p>
                      {(member.forum_location.city || member.forum_location.state) && (
                        <p className="text-sm text-gray-500">
                          {[member.forum_location.city, member.forum_location.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {member.email && !isOwnProfile && (
                  <div className="flex items-start gap-3">
                    <Mail className="text-gray-400 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <a 
                        href={`mailto:${member.email}`}
                        className="font-medium text-navy hover:underline"
                      >
                        {member.email}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Family Info */}
          {isRedacted('family') && privateSection('Family')}
          {!isRedacted('family') && (member.family_status || member.has_children) && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                <Heart size={20} />
                Family
              </h2>
              <div className="space-y-3">
                {member.family_status && (
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className="font-medium text-gray-800 capitalize">{member.family_status}</p>
                  </div>
                )}
                {member.has_children && (
                  <div>
                    <p className="text-sm text-gray-500">Children</p>
                    <p className="font-medium text-gray-800">
                      {member.num_children ? `${member.num_children} ${member.num_children === 1 ? 'child' : 'children'}` : 'Yes'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Hobbies & Interests */}
          {!isRedacted('bio') && member.hobbies_interests && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                <Calendar size={20} />
                Hobbies & Interests
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap text-sm">{member.hobbies_interests}</p>
            </div>
          )}
        </div>
      </div>

      {showMeetingModal && (
        <RequestMeetingModal
          inviteeId={member.id}
          inviteeName={member.full_name}
          onClose={() => setShowMeetingModal(false)}
        />
      )}
    </div>
  );
}
